import {
  FrontendApi,
  LoginFlow,
  Session,
  SuccessfulNativeLogin,
  UpdateLoginFlowBody,
} from "@ory/kratos-client";
import { isNumber, isObject } from "ahooks/es/utils";
import {
  getRedirectBrowserTo,
  isFlowInstance,
  isSessionAlreadyAvailableError,
} from "../../../api";

export async function initiateLoginFlow(
  api: FrontendApi,
  options: {
    returnTo?: string;
    loginChallenge?: string;
    aal?: string;
    refresh?: boolean;
  }
): Promise<LoginFlow | null> {
  return api
    .createBrowserLoginFlow({
      returnTo: options.returnTo,
      loginChallenge: options.loginChallenge,
      aal: options.aal,
      refresh: options.refresh,
    })
    .then((res) => res.data)
    .catch((err) => {
      if (isSessionAlreadyAvailableError(err?.response.data)) {
        return Promise.resolve(null);
      }
      return Promise.reject(err);
    });
}

export async function getLoginFlow(
  api: FrontendApi,
  flowId: string
): Promise<LoginFlow> {
  return api
    .getLoginFlow({
      id: flowId,
    })
    .then((res) => res.data);
}

export async function getOrInitiateLoginFlow(
  api: FrontendApi,
  options: {
    flowId?: string;
    returnTo?: string;
    loginChallenge?: string;
    aal?: string;
    refresh?: boolean;
  }
): Promise<LoginFlow | null> {
  if (options?.flowId) {
    return getLoginFlow(api, options.flowId).catch(() =>
      initiateLoginFlow(api, {
        loginChallenge: options.loginChallenge,
        returnTo: options.returnTo,
        aal: options.aal,
        refresh: options.refresh,
      })
    );
  } else {
    return initiateLoginFlow(api, {
      returnTo: options.returnTo,
      loginChallenge: options.loginChallenge,
      aal: options.aal,
      refresh: options.refresh,
    });
  }
}

export async function submitLogin(
  api: FrontendApi,
  flowId: string,
  updateLoginFlowBody: UpdateLoginFlowBody
): Promise<[SuccessfulNativeLogin | null, LoginFlow | null, number | null]> {
  try {
    const res = await api.updateLoginFlow({
      flow: flowId,
      updateLoginFlowBody,
    });
    return [res.data, null, null];
  } catch (err) {
    const response = isObject(err) ? err.response?.data : null;
    const errorCode =
      isObject(err) && isNumber(err.response?.data.error?.code)
        ? err.response.data.error.code
        : null;
    const redirectUrl = getRedirectBrowserTo(response);
    if (redirectUrl) {
      document.location = redirectUrl;
    } else if (isFlowInstance<LoginFlow>(response)) {
      return [null, response, errorCode];
    }
    return [null, null, errorCode];
  }
}

export async function shouldNavigateToAal2(api: FrontendApi): Promise<boolean> {
  try {
    const res = await api.toSession();
    if (
      res.data.authentication_methods?.some((method) => method.aal === "aal2")
    ) {
      return true;
    }
    return false;
  } catch (err: any) {
    if (err.response?.status === 403) {
      return true;
    }
    return false;
  }
}

export async function toSession(api: FrontendApi): Promise<Session> {
  const res = await api.toSession();
  return res.data;
}
